import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLive } from '../../live.jsx';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import { Modal, Avatar, StatusBadge, PERM_GROUPS, ROLES, COUNTRIES, countryName, langName, fmtDate } from './ws.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// "Équipe / Users" — a company's users (or, in Admin > Représentants PME,
// PME Trade Canada's own representatives: the same API, company key 0).
// Owner: full rights, can't be removed, can transfer ownership.
// company: a super admin fixing another company's team (Admin › Members ›
// company › Team, Admin › Users) — the same screen, every call with ?company=.
const TeamCtx = createContext(null);
const withCompany = (path, company) => (company ? `${path}${path.includes('?') ? '&' : '?'}company=${company}` : path);
const useTeamUrl = () => { const company = useContext(TeamCtx); return (path) => withCompany(path, company); };

export default function TeamPage({ admin = false, company = null }) {
  return <TeamCtx.Provider value={company}><TeamScreen admin={admin} company={company} /></TeamCtx.Provider>;
}

function TeamScreen({ admin, company }) {
  const { t, lang, errorText } = useI18n();
  const { user, signOut, openMemberAccount } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('users');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState(null); // member | 'new'
  const [remove, setRemove] = useState(null);
  const [transfer, setTransfer] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const url = useTeamUrl();

  const load = useCallback(() => api(url('/team')).then((d) => { setData(d); setError(''); }).catch((e) => setError(errorText(e))), [errorText, company]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useLive(['account'], () => { load(); });
  const say = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  if (!data) return <div className="ws-page">{error ? <Alert>{error}</Alert> : <p className="muted">{t('common.loading')}</p>}</div>;
  const manage = data.me.manage;
  const active = data.members.filter((m) => m.status === 'active');

  async function act(m, body, done) {
    try { await api(url(`/team/members/${m.id}`), { method: 'PATCH', body }); say(done); load(); } catch (e) { setError(errorText(e)); }
  }
  async function resetLogin(m) {
    if (!window.confirm(t('team.resetLoginConfirm', { name: m.name }))) return;
    try { const r = await api(url(`/team/members/${m.id}/reset-login`), { method: 'POST' }); say(r.emailed ? t('team.resetLoginDone', { email: m.email }) : t('team.notEmailed')); } catch (e) { setError(errorText(e)); }
  }
  async function openAccount(m) {
    if (!window.confirm(t('team.openConfirm', { name: m.name }))) return;
    try { await openMemberAccount(m.id); } catch (e) { setError(errorText(e)); }
  }
  async function setOwnerAccess(on) {
    try { await api(url('/team/settings'), { method: 'PUT', body: { ownerAccess: on } }); say(t('team.saved')); load(); } catch (e) { setError(errorText(e)); }
  }
  async function cancelInvite(m) {
    if (!window.confirm(t('team.cancelInviteConfirm', { email: m.email }))) return;
    try { await api(url(`/team/members/${m.id}`), { method: 'DELETE', body: { reassignTo: null } }); say(t('team.inviteCancelled')); load(); } catch (e) { setError(errorText(e)); }
  }
  async function resend(m) {
    try { const r = await api(url(`/team/members/${m.id}/resend`), { method: 'POST' }); say(r.emailed ? t('team.resent') : t('team.notEmailed')); load(); } catch (e) { setError(errorText(e)); }
  }

  return (
    <div className="ws-page">
      <div className="acc-head">{!company && <TutorialSlot place="ws.team" />}
        <div>
          <h1>{admin ? t('team.pmeTitle') : company ? t('team.adminTitle', { company: data.company.name }) : t('team.title')}</h1>
          <p className="muted">{admin ? t('team.pmeLead') : t('team.lead', { company: data.company.name })}</p>
        </div>
        {manage && <div className="acc-head-actions"><button type="button" className="btn btn-primary" onClick={() => setEdit('new')}>+ {t('team.add')}</button></div>}
      </div>
      <Alert>{error}</Alert>
      {msg && <p className="callout callout-success" role="status">{msg}</p>}
      {(admin || data.company.pme) && user.role === 'admin' && <PmeCatalogue superAdmin={!!user.isSuperAdmin} />}
      {/* "Ouvrir son compte": the owner's company setting; members are told when it's on. */}
      {!admin && !company && data.me.isOwner && !user.impersonation && (
        <label className="field-checkbox ws-card" style={{ display: 'flex' }}>
          <input type="checkbox" checked={!!data.settings?.ownerAccess} onChange={(e) => setOwnerAccess(e.target.checked)} />
          <span><strong>{t('team.ownerAccess')}</strong><br /><span className="muted small">{t('team.ownerAccessHint')}</span></span>
        </label>
      )}
      {!data.me.isOwner && data.settings?.ownerAccess && <p className="callout callout-info small">{t('team.ownerAccessNotice')}</p>}
      <div className="ws-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'users'} onClick={() => setTab('users')}>{t('team.tabUsers')} ({data.members.length + data.owners.length})</button>
        <button type="button" role="tab" aria-selected={tab === 'roles'} onClick={() => setTab('roles')}>{t('team.tabRoles')}</button>
      </div>

      {tab === 'users' && (
        <>
          <ul className="ws-members">
            {data.owners.map((o) => (
              <li key={`o${o.id}`} className="ws-member">
                <Avatar name={o.name} size={48} />
                <div className="ws-member-main">
                  <strong>{o.name}</strong>
                  <span className="muted small ws-ellipsis">{o.email}</span>
                  <span><span className="ws-badge ws-role-owner">{t('team.role.owner')}</span></span>
                  <ProductsCount n={o.products} userId={o.id} link={!admin && manage} />
                  <MailboxStatus box={o.mailbox} />
                  {!admin && data.me.isOwner && (!user.impersonation || company) && active.length > 0 && (
                    <div className="ws-member-tools"><button type="button" className="btn btn-link btn-sm" onClick={() => setTransfer(true)}>{t('team.transfer')}</button></div>
                  )}
                </div>
              </li>
            ))}
            {data.members.map((m) => (
              <li key={m.id} className={`ws-member${m.status !== 'active' ? ' is-off' : ''}`}>
                <Avatar name={m.name} photoUrl={m.photoUrl} size={48} />
                <div className="ws-member-main">
                  <strong>{m.name}</strong>
                  {m.jobTitle && <span className="small">{m.jobTitle}</span>}
                  {m.email && <span className="muted small ws-ellipsis">{m.email}{m.phone ? ` · ${m.phone}` : ''}</span>}
                  <span className="muted small">{[m.office, m.country && countryName(m.country, lang), m.languages.map((l) => langName(l, lang)).join(', ')].filter(Boolean).join(' · ')}</span>
                  <span className="ws-row">
                    <span className={`ws-badge ws-role-${m.role}`}>{t(`team.role.${m.role}`)}</span>
                    <StatusBadge kind="member" value={m.status} />
                    {m.showPublic && <span className="ws-badge">{t('team.public')}</span>}
                  </span>
                  <span className="muted small">{m.status === 'pending' ? t('team.invitedOn', { date: fmtDate(m.invitedAt, lang) }) : m.lastLoginAt ? t('team.lastLogin', { date: fmtDate(m.lastLoginAt, lang, true) }) : t('team.neverLoggedIn')}</span>
                  {(m.invitedBy || m.acceptedAt) && <span className="muted small">{[m.invitedBy && t('team.invitedBy', { name: m.invitedBy }), m.acceptedAt && t('team.joinedOn', { date: fmtDate(m.acceptedAt, lang) })].filter(Boolean).join(' · ')}</span>}
                  {manage && m.customers > 0 && <span className="small">{t('team.customersN', { n: m.customers })}</span>}
                  <ProductsCount n={m.products} userId={m.userId} link={!admin && manage} />
                  <MailboxStatus box={m.mailbox} />
                  {(manage || m.userId === data.me.personId) && (
                    <div className="ws-member-tools">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEdit(m)}>{t('team.edit')}</button>
                      {manage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEmailFor(m)}>{m.status === 'pending' ? t('team.fixEmail') : t('team.changeEmail')}</button>}
                      {manage && m.status === 'pending' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => resend(m)}>{t('team.resend')}</button>}
                      {manage && m.status === 'pending' && <button type="button" className="btn btn-link btn-sm is-danger" onClick={() => cancelInvite(m)}>{t('team.cancelInvite')}</button>}
                      {manage && m.status === 'active' && m.userId !== data.me.personId && <button type="button" className="btn btn-ghost btn-sm" onClick={() => resetLogin(m)}>{t('team.resetLogin')}</button>}
                      {data.me.canOpen && data.settings?.ownerAccess && m.status === 'active' && m.userId !== data.me.personId && (data.me.isOwner || m.role !== 'admin') && !user.impersonation && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openAccount(m)}>👁 {t('team.openAccount')}</button>
                      )}
                      {manage && m.status === 'active' && m.userId !== data.me.personId && <button type="button" className="btn btn-ghost btn-sm" onClick={() => act(m, { status: 'suspended' }, t('team.suspended'))}>{t('team.suspend')}</button>}
                      {manage && m.status === 'suspended' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => act(m, { status: 'active' }, t('team.reactivated'))}>{t('team.reactivate')}</button>}
                      {manage && m.userId !== data.me.personId && m.status !== 'pending' && <button type="button" className="btn btn-link btn-sm is-danger" onClick={() => setRemove(m)}>{t('team.remove')}</button>}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {!data.members.length && <p className="muted">{t('team.empty')}</p>}
          <p className="ws-hint">{t('team.oneCompany')}</p>
        </>
      )}

      {tab === 'roles' && <RolesMatrix data={data} onSaved={(roles) => { setData({ ...data, roles }); say(t('team.rolesSaved')); }} onError={(e) => setError(errorText(e))} />}

      {edit && <MemberForm data={data} member={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={(m) => { setEdit(null); say(m); load(); }} />}
      {remove && <RemoveMember data={data} member={remove} onClose={() => setRemove(null)} onDone={() => { setRemove(null); say(t('team.removed')); load(); }} />}
      {transfer && <TransferOwner data={data} onClose={() => setTransfer(false)} onDone={() => { if (company) { setTransfer(false); say(t('team.transferred')); load(); } else { signOut(); window.location.assign('/login'); } }} />}
      {emailFor && <EmailChange member={emailFor} superAdmin={!!data.me.superAdmin} onClose={() => setEmailFor(null)} onDone={(m) => { setEmailFor(null); say(m); load(); }} />}
    </div>
  );
}

// "Boîte courriel : ✔ connectée / — non connectée" (never the settings).
function MailboxStatus({ box }) {
  const { t } = useI18n();
  if (!box) return null;
  return (
    <span className={`small${box.connected ? '' : ' muted'}`}>
      {t('team.mailbox.label')} : {box.connected ? `✔ ${t('team.mailbox.on')}` : `— ${t('team.mailbox.off')}`}{box.connected && box.email ? ` · ${box.email}` : ''}
    </span>
  );
}

// "Produits : n" — the products the user added (the owner opens the list).
function ProductsCount({ n, userId, link }) {
  const { t } = useI18n();
  const text = t('team.productsN', { n: n || 0 });
  return <span className="small">{link && n ? <Link to={`/account/products?by=${userId}`}>{text} →</Link> : <span className="muted">{text}</span>}</span>;
}

// The "Produits" switch: one choice that writes the product permissions.
export const PRODUCT_MODES = {
  none: { 'products.view': false, 'products.create': false, 'products.edit': false, 'products.delete': false, 'products.all': false },
  view: { 'products.view': true, 'products.create': false, 'products.edit': false, 'products.delete': false, 'products.all': false },
  own: { 'products.view': true, 'products.create': true, 'products.edit': true, 'products.delete': true, 'products.all': false },
  all: { 'products.view': true, 'products.create': true, 'products.edit': true, 'products.delete': true, 'products.all': true },
};
export const productMode = (p) => (p['products.all'] && p['products.create'] ? 'all' : p['products.create'] ? 'own' : p['products.view'] ? 'view' : 'none');

// Role × permission table, editable by "users.manage".
function RolesMatrix({ data, onSaved, onError }) {
  const { t } = useI18n();
  const [roles, setRoles] = useState(data.roles);
  const [busy, setBusy] = useState('');
  const manage = data.me.manage;
  const dirty = (r) => JSON.stringify(roles[r]) !== JSON.stringify(data.roles[r]);
  async function save(r) {
    setBusy(r);
    try { const d = await api(`/team/roles/${r}`, { method: 'PUT', body: { perms: roles[r] } }); onSaved(d.roles); } catch (e) { onError(e); } finally { setBusy(''); }
  }
  return (
    <>
      <p className="ws-hint">{t('team.rolesLead')}</p>
      <div className="ws-table-wrap">
        <table className="ws-perms">
          <thead><tr><th>{t('team.permission')}</th><th>{t('team.role.owner')}</th>{ROLES.map((r) => <th key={r}>{t(`team.role.${r}`)}</th>)}</tr></thead>
          <tbody>
            {PERM_GROUPS.map(([g, perms]) => (
              <React.Fragment key={g}>
                <tr className="ws-perm-group"><td colSpan={ROLES.length + 2}>{t(`team.permGroup.${g}`)}</td></tr>
                {perms.map((p) => (
                  <tr key={p}>
                    <td>{t(`team.perm.${p.replace('.', '_')}`)}</td>
                    <td>✓</td>
                    {ROLES.map((r) => (
                      <td key={r}><input type="checkbox" checked={!!roles[r][p]} disabled={!manage} aria-label={`${t(`team.role.${r}`)} — ${t(`team.perm.${p.replace('.', '_')}`)}`}
                        onChange={(e) => setRoles({ ...roles, [r]: { ...roles[r], [p]: e.target.checked } })} /></td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {manage && (
              <tr><td>{t('team.saveRole')}</td><td />{ROLES.map((r) => <td key={r}><button type="button" className="btn btn-primary btn-sm" disabled={!dirty(r) || busy === r} onClick={() => save(r)}>{t('common.save')}</button></td>)}</tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

const blank = { firstName: '', lastName: '', email: '', phone: '', whatsapp: '', jobTitle: '', office: '', country: '', languages: '', role: 'rep', showPublic: false, perms: {} };
function MemberForm({ data, member, onClose, onSaved }) {
  const { t, lang, errorText } = useI18n();
  const manage = data.me.manage;
  const [f, setF] = useState(member ? { ...blank, ...member, languages: member.languages.join(', '), perms: member.perms || {} } : { ...blank, lang });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(member?.photoUrl || null);
  const [reassignTo, setReassignTo] = useState('');
  const url = useTeamUrl();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const roleDefault = (p) => !!data.roles[f.role]?.[p];
  const eff = (p) => (p in f.perms ? f.perms[p] : roleDefault(p));
  const mode = productMode(Object.fromEntries(Object.keys(PRODUCT_MODES.all).concat('products.publish').map((p) => [p, eff(p)])));
  // The switch writes explicit values (exceptions) only where they differ from the role.
  const setMode = (m) => setF((x) => {
    const perms = { ...x.perms };
    for (const [p, v] of Object.entries(PRODUCT_MODES[m])) { if (v === !!data.roles[x.role]?.[p]) delete perms[p]; else perms[p] = v; }
    if (m === 'none' || m === 'view') { if (data.roles[x.role]?.['products.publish']) perms['products.publish'] = false; else delete perms['products.publish']; }
    return { ...x, perms };
  });
  const setPublish = (v) => setF((x) => { const perms = { ...x.perms }; if (v === !!data.roles[x.role]?.['products.publish']) delete perms['products.publish']; else perms['products.publish'] = v; return { ...x, perms }; });
  const permValue = (p) => (p in f.perms ? (f.perms[p] ? 'allow' : 'deny') : 'role');
  const setPerm = (p, v) => setF((x) => { const perms = { ...x.perms }; if (v === 'role') delete perms[p]; else perms[p] = v === 'allow'; return { ...x, perms }; });
  async function save(e) {
    e.preventDefault(); setBusy(true); setError('');
    const body = { firstName: f.firstName, lastName: f.lastName, phone: f.phone, ...(member ? { whatsapp: f.whatsapp } : {}), jobTitle: f.jobTitle, office: f.office, country: f.country, languages: f.languages, showPublic: f.showPublic,
      ...(manage ? { role: f.role, perms: f.perms } : {}), ...(member ? {} : { email: f.email, lang: f.lang }) };
    try {
      if (member) { await api(url(`/team/members/${member.id}`), { method: 'PATCH', body }); onSaved(t('team.saved')); } else {
        const r = await api(url('/team/members'), { method: 'POST', body });
        onSaved(r.emailed ? t('team.invited', { email: f.email }) : t('team.invitedNoEmail'));
      }
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  async function reassign() {
    try { const r = await api(url(`/team/members/${member.id}/reassign`), { method: 'POST', body: { to: reassignTo ? Number(reassignTo) : null } }); onSaved(t('team.reassigned', { n: r.moved })); } catch (e) { setError(errorText(e)); }
  }
  async function upload(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    try { const r = await api(url(`/team/members/${member.id}/photo`), { method: 'POST', form: fd }); setPhoto(r.photoUrl); } catch (e) { setError(errorText(e)); }
  }
  return (
    <Modal title={member ? t('team.editTitle', { name: member.name }) : t('team.addTitle')} onClose={onClose} wide>
      <form onSubmit={save} className="ws-form">
        {member && (
          <div className="ws-full ws-row">
            <Avatar name={member.name} photoUrl={photo} size={56} />
            <label className="btn btn-ghost btn-sm">{t('team.photo')}<input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => upload(e.target.files[0])} /></label>
          </div>
        )}
        <label className="field"><span className="field-label">{t('team.f.firstName')}</span><input value={f.firstName} onChange={(e) => set('firstName', e.target.value)} required maxLength={80} /></label>
        <label className="field"><span className="field-label">{t('team.f.lastName')}</span><input value={f.lastName} onChange={(e) => set('lastName', e.target.value)} required maxLength={80} /></label>
        {!member && <label className="field"><span className="field-label">{t('team.f.email')}</span><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} required autoComplete="off" /></label>}
        <label className="field"><span className="field-label">{t('team.f.phone')}</span><input value={f.phone} onChange={(e) => set('phone', e.target.value)} maxLength={30} /></label>
        {member && <label className="field"><span className="field-label">WhatsApp</span><input type="tel" value={f.whatsapp || ''} onChange={(e) => set('whatsapp', e.target.value)} maxLength={30} placeholder="+1 438 555 0100" /></label>}
        {member && <p className="ws-hint ws-full">{t('team.f.emailIs', { email: member.email })}</p>}
        <label className="field ws-full"><span className="field-label">{t('team.f.jobTitle')}</span><input value={f.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} maxLength={120} placeholder={t('team.f.jobTitlePh')} /></label>
        <label className="field"><span className="field-label">{t('team.f.office')}</span><input value={f.office} onChange={(e) => set('office', e.target.value)} maxLength={120} /></label>
        <label className="field"><span className="field-label">{t('team.f.country')}</span>
          <select value={f.country} onChange={(e) => set('country', e.target.value)}><option value="">—</option>{COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c, lang)}</option>)}</select></label>
        <label className="field"><span className="field-label">{t('team.f.languages')}</span><input value={f.languages} onChange={(e) => set('languages', e.target.value)} placeholder="fr, en, hi" /><span className="field-hint">{t('team.f.languagesHint')}</span></label>
        {manage && (
          <label className="field"><span className="field-label">{t('team.f.role')}</span>
            <select value={f.role} onChange={(e) => set('role', e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{t(`team.role.${r}`)}</option>)}</select></label>
        )}
        {!member && (
          <label className="field"><span className="field-label">{t('team.f.lang')}</span>
            <select value={f.lang} onChange={(e) => set('lang', e.target.value)}><option value="fr">Français</option><option value="en">English</option></select></label>
        )}
        {!data.company.pme && (
          <label className="field-checkbox ws-full"><input type="checkbox" checked={f.showPublic} onChange={(e) => set('showPublic', e.target.checked)} /><span>{t('team.f.showPublic')}</span></label>
        )}
        {manage && (
          <fieldset className="ws-full ws-products-switch">
            <legend>{t('team.products.title')}</legend>
            {['none', 'view', 'own', 'all'].map((m) => (
              <label key={m} className="field-checkbox"><input type="radio" name="products-mode" checked={mode === m} onChange={() => setMode(m)} /><span>{t(`team.products.${m}`)}</span></label>
            ))}
            {(mode === 'own' || mode === 'all') && (
              <label className="field-checkbox ws-publish"><input type="checkbox" checked={eff('products.publish')} onChange={(e) => setPublish(e.target.checked)} /><span>{t('team.products.publish')}</span></label>
            )}
            <span className="ws-hint">{t('team.products.hint')}</span>
          </fieldset>
        )}
        {manage && (
          <details className="ws-full">
            <summary><strong>{t('team.overrides')}</strong> <span className="muted small">({Object.keys(f.perms).length})</span></summary>
            <p className="ws-hint">{t('team.overridesLead')}</p>
            <div className="ws-table-wrap">
              <table className="ws-perms">
                <tbody>
                  {PERM_GROUPS.map(([g, perms]) => (
                    <React.Fragment key={g}>
                      <tr className="ws-perm-group"><td colSpan={2}>{t(`team.permGroup.${g}`)}</td></tr>
                      {perms.map((p) => (
                        <tr key={p}>
                          <td className={permValue(p) !== 'role' ? 'ws-perm-diff' : ''}>{t(`team.perm.${p.replace('.', '_')}`)}</td>
                          <td>
                            <select value={permValue(p)} onChange={(e) => setPerm(p, e.target.value)} aria-label={t(`team.perm.${p.replace('.', '_')}`)}>
                              <option value="role">{t('team.byRole', { v: roleDefault(p) ? t('team.yes') : t('team.no') })}</option>
                              <option value="allow">{t('team.allow')}</option>
                              <option value="deny">{t('team.deny')}</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
        {!member && <p className="ws-hint ws-full">{t('team.inviteHint')}</p>}
        {member && manage && member.customers > 0 && (
          <fieldset className="ws-full">
            <legend>{t('team.customersN', { n: member.customers })}</legend>
            <div className="ws-row">
              <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} aria-label={t('team.reassignTo')}>
                <option value="">{t('team.nobody')}</option>
                {[...data.owners.map((o) => ({ id: o.id, name: o.name })), ...data.members.filter((x) => x.status === 'active' && x.id !== member.id).map((x) => ({ id: x.userId, name: x.name }))].map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <button type="button" className="btn btn-ghost btn-sm" onClick={reassign}>{t('team.reassignBtn')}</button>
            </div>
          </fieldset>
        )}
        <div className="ws-full"><Alert>{error}</Alert></div>
        <div className="ws-actions ws-full">
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{member ? t('common.save') : t('team.sendInvite')}</button>
        </div>
      </form>
    </Modal>
  );
}

function RemoveMember({ data, member, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const url = useTeamUrl();
  const [to, setTo] = useState('');
  const [error, setError] = useState('');
  const options = [...data.owners.map((o) => ({ id: o.id, name: o.name })), ...data.members.filter((m) => m.status === 'active' && m.id !== member.id).map((m) => ({ id: m.userId, name: m.name }))];
  async function go() {
    try { await api(url(`/team/members/${member.id}`), { method: 'DELETE', body: { reassignTo: to ? Number(to) : null } }); onDone(); } catch (e) { setError(errorText(e)); }
  }
  return (
    <Modal title={t('team.removeTitle', { name: member.name })} onClose={onClose}>
      <p>{t('team.removeLead')}</p>
      <p className="ws-hint">{t('team.removeKeeps')}</p>
      <label className="field"><span className="field-label">{t('team.reassignTo')}</span>
        <select value={to} onChange={(e) => setTo(e.target.value)}><option value="">{t('team.nobody')}</option>{options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></label>
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="button" className="btn btn-danger" onClick={go}>{t('team.remove')}</button></div>
    </Modal>
  );
}

// Change a member's sign-in email: a pending invitation is corrected and sent
// again; an active user confirms with the code sent to the NEW address. A
// super admin may apply it at once (override).
function EmailChange({ member, superAdmin, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const url = useTeamUrl();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [override, setOverride] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  async function send(e) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await api(url(`/team/members/${member.id}/email`), { method: 'POST', body: { email, override } });
      if (r.applied) onDone(member.status === 'pending' ? t('team.emailFixedInvite', { email }) : t('team.emailChanged', { email }));
      else setStep('code');
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  async function confirm(e) {
    e.preventDefault(); setBusy(true); setError(null);
    try { await api(url(`/team/members/${member.id}/email/confirm`), { method: 'POST', body: { email, code } }); onDone(t('team.emailChanged', { email })); } catch (err) { setError(err); } finally { setBusy(false); }
  }
  const inUse = error?.code === 'team_email_in_use';
  return (
    <Modal title={t('team.emailTitle', { name: member.name })} onClose={onClose}>
      <p className="muted small">{t('team.emailCurrent', { email: member.email })}</p>
      {step === 'email' ? (
        <form onSubmit={send} className="ws-form">
          <label className="field ws-full"><span className="field-label">{t('team.emailNew')}</span><input type="email" required autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <p className="ws-hint ws-full">{member.status === 'pending' ? t('team.emailHintPending') : t('team.emailHintCode')}</p>
          {superAdmin && member.status !== 'pending' && <label className="field-checkbox ws-full"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /><span>{t('team.emailOverride')}</span></label>}
          {inUse ? (
            <div className="callout callout-warning ws-full small">
              <p><strong>{t('team.emailInUse', { email })}</strong></p>
              <ul><li>{t('team.emailInUseOther')}</li><li>{superAdmin ? t('team.emailInUseMergeAdmin') : t('team.emailInUseMerge')}</li></ul>
            </div>
          ) : error && <div className="ws-full"><Alert>{errorText(error)}</Alert></div>}
          <div className="ws-actions ws-full"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="submit" className="btn btn-primary" disabled={busy || !email}>{member.status === 'pending' ? t('team.emailFixAndResend') : override ? t('team.emailApply') : t('team.emailSendCode')}</button></div>
        </form>
      ) : (
        <form onSubmit={confirm} className="ws-form">
          <p className="ws-full">{t('team.emailCodeSent', { email })}</p>
          <label className="field ws-full"><span className="field-label">{t('team.emailCode')}</span><input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></label>
          {error && <div className="ws-full"><Alert>{errorText(error)}</Alert></div>}
          <div className="ws-actions ws-full"><button type="button" className="btn btn-ghost" onClick={() => setStep('email')}>{t('team.emailBack')}</button><button type="submit" className="btn btn-primary" disabled={busy || code.length < 6}>{t('team.emailConfirm')}</button></div>
        </form>
      )}
    </Modal>
  );
}

function TransferOwner({ data, onClose, onDone }) {
  const { t, errorText } = useI18n();
  const url = useTeamUrl();
  const [id, setId] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState('');
  async function go() {
    try { await api(url('/team/transfer'), { method: 'POST', body: { memberId: Number(id) } }); onDone(); } catch (e) { setError(errorText(e)); }
  }
  return (
    <Modal title={t('team.transferTitle')} onClose={onClose}>
      <p>{t('team.transferLead')}</p>
      <label className="field"><span className="field-label">{t('team.newOwner')}</span>
        <select value={id} onChange={(e) => setId(e.target.value)}><option value="">—</option>{data.members.filter((m) => m.status === 'active').map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
      <label className="field-checkbox"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} /><span>{t('team.transferConfirm')}</span></label>
      <Alert>{error}</Alert>
      <div className="ws-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button><button type="button" className="btn btn-danger" disabled={!id || !confirm} onClick={go}>{t('team.transfer')}</button></div>
    </Modal>
  );
}

// "Journal d'activité" (Owner / Admin).
export function ActivityLog({ admin = false }) {
  const { t, lang, errorText } = useI18n();
  const [items, setItems] = useState(null);
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(1);
  const [people, setPeople] = useState([]);
  const [actor, setActor] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { api('/team').then((d) => setPeople([...d.owners.map((o) => ({ id: o.id, name: o.name })), ...d.members.map((m) => ({ id: m.userId, name: m.name }))])).catch(() => {}); }, []);
  useEffect(() => {
    api(`/team/activity?page=${page}${actor ? `&actor=${actor}` : ''}`).then((d) => { setItems((cur) => (page === 1 ? d.items : [...(cur || []), ...d.items])); setMore(d.more); }).catch((e) => setError(errorText(e)));
  }, [page, actor, errorText]);
  return (
    <div className="ws-page">
      <div className="acc-head"><TutorialSlot place="ws.team.activity" /><div><h1>{t('team.activityTitle')}</h1><p className="muted">{admin ? t('team.activityLeadPme') : t('team.activityLead')}</p></div></div>
      <Alert>{error}</Alert>
      <div className="acc-toolbar">
        <select value={actor} onChange={(e) => { setActor(e.target.value); setPage(1); setItems(null); }} aria-label={t('team.who')}>
          <option value="">{t('team.everyone')}</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      {!items ? <p className="muted">{t('common.loading')}</p> : (
        <div className="ws-table-wrap">
          <table className="ws-table ws-table-cards">
            <thead><tr><th>{t('team.when')}</th><th>{t('team.who')}</th><th>{t('team.what')}</th><th>{t('team.details')}</th></tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td data-label={t('team.when')}>{fmtDate(a.at, lang, true)}</td>
                  <td data-label={t('team.who')}>{a.actor || t('team.customer')}{a.impersonatedBy && <span className="ws-badge"> {t('team.viaAdmin', { name: a.impersonatedBy })}</span>}</td>
                  <td className="ws-td-main">{t(`team.act.${a.action.replace(/[.\s/:]+/g, '_')}`) !== `team.act.${a.action.replace(/[.\s/:]+/g, '_')}` ? t(`team.act.${a.action.replace(/[.\s/:]+/g, '_')}`) : a.action}</td>
                  <td data-label={t('team.details')} className="small">{a.details || ''}</td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={4} className="muted">{t('team.noActivity')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {more && <button type="button" className="btn btn-ghost" onClick={() => setPage(page + 1)}>{t('team.more')}</button>}
    </div>
  );
}

// Admin > Représentants PME: "Compte entreprise PME" — the member account the
// super admin uses in "Mon compte" for PME Trade Canada (owner). PME
// representatives are its users; their products are filed under it.
function PmeCatalogue({ superAdmin }) {
  const { t, errorText } = useI18n();
  const [current, setCurrent] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { api('/team/pme-catalogue').then((d) => setCurrent(d.current)).catch(() => setCurrent(null)); }, []);
  useEffect(() => {
    if (!open || !superAdmin) return undefined;
    const tm = setTimeout(() => api(`/team/pme-catalogue/candidates?q=${encodeURIComponent(q)}`).then(setList).catch(() => {}), 250);
    return () => clearTimeout(tm);
  }, [open, q, superAdmin]);
  async function save(userId) {
    setError('');
    try { const d = await api('/team/pme-catalogue', { method: 'PUT', body: { userId } }); setCurrent(d.current); setOpen(false); setMsg(d.moved || d.removed ? t('team.catalogue.moved', { n: d.moved }) : ''); } catch (e) { setError(errorText(e)); }
  }
  if (current === undefined) return null;
  return (
    <section className={`ws-card${current ? '' : ' callout-warning'}`}>
      <div className="ws-row">
        <div className="ws-grow">
          <h3>{t('team.catalogue.title')}</h3>
          <p className="ws-hint" style={{ margin: 0 }}>{current ? t('team.catalogue.current', { company: current.company, email: current.email }) : t('team.catalogue.none')}</p>
        </div>
        {superAdmin && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)}>{current ? t('team.catalogue.change') : t('team.catalogue.set')}</button>}
      </div>
      {msg && <p className="callout callout-success small">{msg}</p>}
      {open && (
        <>
          <label className="field"><span className="field-label">{t('team.catalogue.pick')}</span>
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ws.cust.search')} /></label>
          <ul className="ws-list">{list.map((c) => <li key={c.id}><span className="ws-grow">{c.company} <span className="muted small">— {c.email}</span></span><button type="button" className="btn btn-link btn-sm" onClick={() => save(c.id)}>{t('team.catalogue.use')}</button></li>)}</ul>
          <p className="ws-hint">{t('team.catalogue.pickHint')}</p>
          <Alert>{error}</Alert>
        </>
      )}
    </section>
  );
}
